import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import MarketingNav from '@/components/layout/MarketingNav';
import MarketingFooter from '@/components/layout/MarketingFooter';
import { BLOG_POSTS, getPost } from '@/lib/blogPosts';
import Seo from '@/components/Seo';

const ACCENT = {
  cyan: 'text-boosta-cyan bg-boosta-cyan/10 border-boosta-cyan/25',
  yellow: 'text-boosta-yellow bg-boosta-yellow/10 border-boosta-yellow/25',
  emerald: 'text-boosta-emerald bg-boosta-emerald/10 border-boosta-emerald/25',
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!post) return <Navigate to="/blog" replace />;

  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div className="bg-boosta-ink min-h-screen text-white">
      <Seo title={`${post.title} | Boosta Blog`} description={post.excerpt} />
      <MarketingNav />
      <article className="relative pt-32 pb-20 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-boosta-cyan transition-colors mb-8" data-testid="blog-back">
              <ArrowLeft className="w-4 h-4" /> All articles
            </Link>
            <div className="flex items-center gap-3 mb-6">
              <span className={`text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full border ${ACCENT[post.accent]}`}>{post.category}</span>
              <span className="text-xs text-white/40 flex items-center gap-1.5"><Clock className="w-3 h-3" />{post.readTime}</span>
              <span className="text-xs text-white/40">{new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-3d leading-[1.05]" data-testid="blog-post-title">{post.title}</h1>
            <p className="mt-6 text-lg text-white/60 leading-relaxed border-l-2 border-boosta-cyan/50 pl-5">{post.excerpt}</p>
          </motion.div>

          <div className="mt-12 space-y-10">
            {post.sections.map((s, i) => (
              <motion.section key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
                <h2 className="font-display font-bold text-lg md:text-xl tracking-tight mb-4 flex items-center gap-3">
                  <span className="mono text-[11px] text-boosta-yellow/80">{String(i + 1).padStart(2, '0')}</span>{s.h}
                </h2>
                <div className="space-y-4">
                  {s.p.map((para, j) => <p key={j} className="text-white/60 leading-[1.85] text-[15px]">{para}</p>)}
                </div>
              </motion.section>
            ))}
          </div>

          <div className="mt-16 glass rounded-3xl p-8 md:p-10 text-center relative overflow-hidden aurora">
            <div className="relative">
              <h3 className="font-display font-black text-2xl tracking-tight">Put this playbook to work.</h3>
              <p className="mt-3 text-white/55 text-sm max-w-md mx-auto">Generate, test and launch AI-powered ad creative from one command centre.</p>
              <Link to="/register" className="mt-6 btn-primary rounded-full px-6 py-3 text-sm inline-flex items-center gap-2" data-testid="blog-post-cta">
                Start your free trial <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="mt-14">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/40 mb-5">Keep reading</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {others.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group glass glass-hover rounded-2xl p-6" data-testid={`blog-related-${p.slug}`}>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-0.5 rounded-full border ${ACCENT[p.accent]}`}>{p.category}</span>
                  <div className="mt-3 font-display font-bold text-base group-hover:text-boosta-cyan transition-colors leading-snug">{p.title}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>
      <MarketingFooter />
    </div>
  );
}
